
import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { Button } from './../ui/button';
import { Badge } from './../ui/badge';
import { CheckCircle, XCircle, FileCheck, ChevronDown, ChevronUp, AlertTriangle, User, FileText, Eye, Shield, ArrowLeft, X, Wind } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import { Document, Page, pdfjs } from 'react-pdf';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

const ECCNominationApprovalModal = ({
  isOpen,
  onClose,
  nomination,
  refreshNominations,
}) => {
  const [selectedWorkScopes, setSelectedWorkScopes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [openWorkScopeIndex, setOpenWorkScopeIndex] = useState(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const previewContainerRef = useRef(null);
  const [userWorkPermits, setUserWorkPermits] = useState([]);
  const [userWarnings, setUserWarnings] = useState([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [activeScope, setActiveScope] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [openSection, setOpenSection] = useState('authorizationLevels');
  const [approvedNominations, setApprovedNominations] = useState([]);
  const [isLoadingNominations, setIsLoadingNominations] = useState(false);
  const [nominationsError, setNominationsError] = useState(null);
  const [isAuthLevelOpen, setIsAuthLevelOpen] = useState(true);
  const [openCategoryIdx, setOpenCategoryIdx] = useState(null);
  const [previewDocName, setPreviewDocName] = useState(''); // NOWE: Stan dla nazwy certyfikatu
  const [isNominationPdfOpen, setIsNominationPdfOpen] = useState(true); // NOWE: Stan dla sekcji PDF nominacji

  const [showCodeInput, setShowCodeInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationDoc, setVerificationDoc] = useState(null); // Przechowuje docId, documentUrl, docName
  

  const { user } = useContext(UserContext);
  console.log("nominacja", nomination)
  console.log("approvednominacja", approvedNominations)
  console.log("PDF FILE DEBUG:", nomination?.pdf_file)
  const getColumnWidths = () => {
    if (previewExpanded) {
      return {
        leftCol: 'w-2/6',
        middleCol: 'w-2/6',
        rightCol: 'w-4/6'
      };
    } else {
      return {
        leftCol: 'w-1/2',
        middleCol: 'w-1/2',
        rightCol: 'w-12'
      };
    }
  };

  const columnWidths = getColumnWidths();

  // Reszta useEffect bez zmian
  useEffect(() => {
    if (!nomination) return;

    setSelectedWorkScopes([]);
    setSelectedCategories([]);
    setRejectReason('');
    setOpenWorkScopeIndex(null);
    setActiveScope(nomination.work_scope_data?.[0] || null);
    setOpenSection('authorizationLevels');
    setUserWorkPermits([]);
    setUserWarnings([]);
    setApprovedNominations([]);
    setNominationsError(null);

    const userId = nomination.user;
    const token = localStorage.getItem('access_token');

    if (userId && token && nomination.status === 'signed') {
      axios
        .get(`${API_BASE_URL}/workpermits/by_user/?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => {
          setUserWorkPermits(response.data || []);
        })
        .catch(error => {
          console.error("Error fetching work permits:", error);
        });
    }
  }, [nomination]);

  useEffect(() => {
    if (!nomination || openSection !== 'approvedNominations' || nomination.status !== 'signed') return;

    const userId = nomination.user;
    const token = localStorage.getItem('access_token');

    if (!userId || !token) {
      setNominationsError("Missing user ID or authentication token.");
      return;
    }

    setIsLoadingNominations(true);
    setNominationsError(null);

    axios
      .get(`${API_BASE_URL}/nomination/?user_id=${userId}&status=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        const filteredNominations = (response.data || []).filter(n => n.id !== nomination.id);
        setApprovedNominations(filteredNominations);
        setIsLoadingNominations(false);
      })
      .catch(error => {
        console.error("Error fetching approved nominations:", error);
        setNominationsError("Failed to load approved nominations.");
        setIsLoadingNominations(false);
      });
  }, [nomination, openSection]);

  useEffect(() => {
    if (!nomination || openSection !== 'warnings' || nomination.status !== 'signed') return;

    const userId = nomination.user;
    const token = localStorage.getItem('access_token');

    if (userId && token) {
      axios
        .get(`${API_BASE_URL}/penalty/?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => {
          setUserWarnings(response.data || []);
        })
        .catch(error => {
          console.error("Error fetching warnings:", error);
        });
    }
  }, [nomination, openSection]);

  useEffect(() => {
    if (!isOpen) {
      resetPreview();
    }
  }, [isOpen]);

  const toggleAuthLevel = () => {
    setIsAuthLevelOpen(prev => !prev);
  };

  const toggleCategory = (idx) => {
    setOpenCategoryIdx(prev => (prev === idx ? null : idx));
  };

  const getRequiredDocumentsForActiveScope = () => {
    if (!activeScope) return [];
    const scopeDocs = activeScope.required_documents || [];
    return scopeDocs
      .map(docId => nomination?.qualifications?.find(doc => doc.id === docId))
      .filter(Boolean);
  };

  const getCategoryDocumentsForActiveScope = () => {
    if (!activeScope) return [];
    const scopeDocIds = activeScope.required_documents || [];
    const appliedCategories = getAppliedCategories(activeScope);
    return appliedCategories
      .map(category => {
        const catDocIds = category.required_documents || [];
        const uniqueCatDocIds = catDocIds.filter(docId => !scopeDocIds.includes(docId));
        const catDocs = uniqueCatDocIds
          .map(docId => nomination?.qualifications?.find(doc => doc.id === docId))
          .filter(Boolean);
        return { categoryName: category.name, documents: catDocs };
      })
      .filter(catItem => catItem.documents.length > 0);
  };

  const userDocuments = nomination?.user_data?.qualifications?.map(qualification => ({
    id: qualification.qualification_type,
    name: qualification.name || `Document ${qualification.qualification_type}`,
    status: new Date(qualification.expiry_date) > new Date() ? "Valid" : "Expired",
    expiry_date: qualification.expiry_date,
    certificate_file: qualification.certificate_file,
    qualification_type_data: qualification.qualification_type_data
  })) || [];

  const handleVerifyCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/verify-code/`,
        { code: verificationCode },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Udana weryfikacja - pobierz plik
      setShowCodeInput(false);
      setVerificationCode('');
      setWarningMessage(response.data.message || 'Verification successful');

      if (verificationDoc) {
        const { documentUrl, docName } = verificationDoc;
        const fileResponse = await axios.get(documentUrl, { responseType: 'blob' });
        processFile(fileResponse.data, docName);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setWarningMessage(error.response?.data?.error || 'Invalid verification code');
      setShowWarningModal(true);
    }
  };

  const handlePreview = async (docId, documentUrl, docName) => {
    setPreviewExpanded(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      // Specjalny przypadek dla PDF nominacji
      if (docId === 'nomination-pdf') {
        // PDF nominacji nie wymaga weryfikacji - pobierz przez API endpoint
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${API_BASE_URL}/nomination/${nomination.id}/download/`,
          { 
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        processFile(response.data, docName);
        return;
      }

      // Znajdź dokument w userDocuments (dla certyfikatów użytkownika)
      const userDoc = userDocuments.find(uDoc => uDoc.id === docId);
      
      console.log("userDoc", userDoc)
      console.log("need_auth", userDoc.qualification_type_data?.need_auth)
      if (!userDoc) {
        setWarningMessage('Document not found');
        setShowWarningModal(true);
        return;
      }

      if (userDoc.qualification_type_data?.need_auth) {
        const token = localStorage.getItem('access_token');
          await axios.post(
            `${API_BASE_URL}/send-verification-email/`,
            {}, // Brak parametrów, backend użyje danych użytkownika z tokenu
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        // Dokument wymaga weryfikacji kodu
        setShowCodeInput(true);
        setVerificationDoc({ docId, documentUrl, docName });
        setWarningMessage('Please enter the verification code to view this document.');
        return;
      }

      // Dokument bez weryfikacji - pobierz bezpośrednio
      const response = await axios.get(documentUrl, { responseType: 'blob' });
      processFile(response.data, docName);
    } catch (error) {
      console.error('Error loading document:', error);
      setWarningMessage(error.response?.data?.error || 'Error loading document');
      setShowWarningModal(true);
    }
  };

  // Funkcja pomocnicza do przetwarzania pliku
  const processFile = async (fileData, docName) => {
    const fileType = fileData.type;

    if (fileType.startsWith('image/')) {
      const imgBlob = fileData;
      const imgURL = URL.createObjectURL(imgBlob);
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      const img = new Image();
      img.onload = () => {
        const imgWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (img.height * imgWidth) / img.width;
        pdf.addImage(img, 'PNG', 0, 0, imgWidth, imgHeight);
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setDocPreviewUrl(pdfUrl);
        setPreviewDocName(docName);
        setPreviewExpanded(true);
      };
      img.src = imgURL;
    } else if (fileType === 'application/pdf') {
      const pdfUrl = URL.createObjectURL(fileData);
      setDocPreviewUrl(pdfUrl);
      setPreviewDocName(docName);
      setPreviewExpanded(true);
    } else {
      setWarningMessage('File preview not supported for this file type');
      setShowWarningModal(true);
    }
  };

  const toggleWorkScope = (id) => {
    setOpenWorkScopeIndex(prev => prev === id ? null : id);
  };

  const handleScopeClick = (scope) => {
    setActiveScope(scope);
    toggleWorkScope(scope.id);
  };

  const getAppliedCategories = (scope) => {
    if (!scope || !scope.categories) return [];
    return scope.categories.filter(category => nomination?.categories?.includes(category.id));
  };

  const checkDocumentsForScopeOrCategory = (scopeDocs, categoryDocs = [], scopeOrCategoryName) => {
    const allRequiredDocs = [...new Set([...scopeDocs, ...categoryDocs])];
    const missingDocs = allRequiredDocs.filter(docId => {
      const userDoc = userDocuments.find(uDoc => uDoc.id === docId);
      return !userDoc || !userDoc.expiry_date || new Date(userDoc.expiry_date) <= new Date();
    });

    if (missingDocs.length > 0) {
      const missingDocNames = missingDocs
        .map(docId => nomination?.qualifications?.find(q => q.id === docId)?.name || `Document ${docId}`)
        .join(', ');
      return `Missing or expired documents for ${scopeOrCategoryName}: ${missingDocNames}`;
    }
    return null;
  };

  const handleToggleWorkScope = (workScopeId, categories) => {
    const isSelected = selectedWorkScopes.includes(workScopeId);
    const scope = nomination.work_scope_data.find(s => s.id === workScopeId);

    if (!isSelected) {
      const scopeDocs = scope.required_documents || [];
      const appliedCategories = getAppliedCategories(scope);
      const allCategoryDocs = appliedCategories.reduce((acc, cat) => {
        return [...acc, ...(cat.required_documents || [])];
      }, []);

      const fullWarning = checkDocumentsForScopeOrCategory(
        scopeDocs,
        allCategoryDocs,
        `${scope.name} (including applied categories)`
      );

      if (fullWarning) {
        setWarningMessage(fullWarning);
        setShowWarningModal(true);
      }
    }

    if (isSelected) {
      setSelectedWorkScopes(prev => prev.filter(id => id !== workScopeId));
      setSelectedCategories(prev => prev.filter(catId => !categories.some(cat => cat.id === catId)));
    } else {
      setSelectedWorkScopes(prev => [...prev, workScopeId]);
      const appliedCategoryIds = getAppliedCategories(scope).map(cat => cat.id);
      setSelectedCategories(prev => [...new Set([...prev, ...appliedCategoryIds])]);
    }
  };

  const handleToggleCategory = (workScopeId, categoryId, categories) => {
    const isSelected = selectedCategories.includes(categoryId);
    const category = categories.find(cat => cat.id === categoryId);
    const scope = nomination.work_scope_data.find(s => s.id === workScopeId);

    if (!isSelected) {
      const scopeDocs = scope.required_documents || [];
      const catDocs = category.required_documents || [];
      const categoryWarning = checkDocumentsForScopeOrCategory(scopeDocs, catDocs, `${scope.name} - ${category.name}`);
      
      if (categoryWarning) {
        setWarningMessage(categoryWarning);
        setShowWarningModal(true);
      }
    }

    let updatedCategories;
    if (isSelected) {
      updatedCategories = selectedCategories.filter(id => id !== categoryId);
      setSelectedCategories(updatedCategories);
    } else {
      updatedCategories = [...selectedCategories, categoryId];
      setSelectedCategories(updatedCategories);
    }

    const anyCategorySelected = categories.some(cat => updatedCategories.includes(cat.id));
    if (anyCategorySelected) {
      setSelectedWorkScopes(prev => prev.includes(workScopeId) ? prev : [...prev, workScopeId]);
    } else {
      setSelectedWorkScopes(prev => prev.filter(id => id !== workScopeId));
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pl-PL');
  };

  const formatDateTime = (dateTime) => new Date(dateTime).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  const resetPreview = () => {
    if (docPreviewUrl) {
      URL.revokeObjectURL(docPreviewUrl);
    }
    setDocPreviewUrl(null);
    setNumPages(null);
    setScale(1.0);
    setPreviewExpanded(false);
    setPreviewDocName(''); // NOWE: Czyszczenie nazwy dokumentu
  };

  const handleApprove = async () => {
    if (!nomination) return;

    if (selectedWorkScopes.length === 0) {
      setWarningMessage('Please select at least one work scope before approving the nomination.');
      setShowWarningModal(true);
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (!userData || !userData.id) {
        throw new Error("No user data found.");
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error("No authentication token found.");
      }
      
      const payload = {
        date_approved: new Date().toISOString(),
        status: "approved",
        approver: userData.id,
        work_scopes: selectedWorkScopes,
        categories: selectedCategories
      };
      
      await axios.put(
        `${API_BASE_URL}/nomination/${nomination.id}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      await refreshNominations();
      setActiveScope(null);
      resetPreview();
      setOpenSection('authorizationLevels');
      onClose(false);
    } catch (error) {
      console.error("Error approving nomination:", error);
      setWarningMessage("Failed to approve nomination. Please try again.");
      setShowWarningModal(true);
    }
  };

  const handleReject = async () => {
    if (!nomination || !rejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (!userData || !userData.id) {
        throw new Error("No user data found.");
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error("No authentication token found.");
      }
      
      const payload = {
        date_rejected: new Date().toISOString(),
        status: "rejected",
        approver: userData.id,
        comment: rejectReason,
      };
      
      await axios.put(
        `${API_BASE_URL}/nomination/${nomination.id}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      await refreshNominations();
      setShowRejectModal(false);
      setActiveScope(null);
      resetPreview();
      setOpenSection('authorizationLevels');
      onClose(false);
    } catch (error) {
      console.error("Error rejecting nomination:", error);
      setWarningMessage("Failed to reject nomination. Please try again.");
      setShowWarningModal(true);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (previewContainerRef.current) {
      const containerWidth = previewContainerRef.current.clientWidth - 20;
      const defaultPageWidth = 595;
      const initialScale = containerWidth / defaultPageWidth;
      setScale(initialScale);
    }
  };

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale * 1.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale / 1.2, 0.5));
  };

  const handleClosePreview = () => {
    resetPreview();
  };

  const handleExpandPreview = () => {
    setPreviewExpanded(!previewExpanded);
  };

  const latestEndDate = userWorkPermits.reduce((latest, wp) => {
    if (!wp.planned_date_end) return latest;
    const currentDate = new Date(wp.planned_date_end);
    return (!latest || currentDate > latest) ? currentDate : latest;
  }, null);

  const requiredDocuments = getRequiredDocumentsForActiveScope();
  const categoryDocuments = getCategoryDocumentsForActiveScope();

  const toggleSection = (section) => {
    setOpenSection(prev => prev === section ? null : section);
    if (previewExpanded && ['approvedNominations', 'workPermits', 'warnings'].includes(section)) {
      setPreviewExpanded(false);
    }
  };

  const toggleNominationPdf = () => {
    setIsNominationPdfOpen(prev => !prev);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'signed':
        return 'bg-brand-500 text-white';
      case 'approved':
        return 'bg-emerald-600 text-white';
      case 'rejected':
        return 'bg-rose-600 text-white';
      default:
        return 'bg-graphite-600 text-white';
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status) {
      case 'signed':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Expired';
    }
  };

  if (!nomination || !isOpen) return null;

  // Obliczenie całkowitej liczby poziomów autoryzacji i platform
  const totalWorkScopes = nomination.work_scope_data?.length || 0;
  const totalCategories = nomination.categories?.length || 0;

  return (
    <div className="fixed inset-0 z-50 bg-steel-50 w-full h-screen flex flex-col">
      <div className="bg-white border-b-2 border-brand-500 px-4 py-3 flex items-center shrink-0 shadow-sm">
        <button
          onClick={() => onClose(false)}
          className="p-2 mr-4 rounded-xl text-graphite-600 hover:bg-steel-100 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-graphite-900">
            Nomination {nomination.nomination_number ? `(${nomination.nomination_number})` : ''}
          </h1>
        </div>
        <button
          onClick={() => onClose(false)}
          className="p-2 rounded-xl text-graphite-500 hover:bg-steel-100 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden p-4 gap-4">
        <div className={`${columnWidths.leftCol} flex flex-col overflow-hidden transition-all duration-300 gap-4`}>
          <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden shadow-sm">
            <div className="p-4">
              <div className="flex items-center mb-3">
                <div className="mr-3">
                  <div className="w-12 h-12 bg-steel-50 rounded-full flex items-center justify-center border-2 border-steel-200 overflow-hidden">
                    <img
                      alt={nomination.user_name || 'User'}
                      className="w-full h-full object-cover"
                      src={nomination.user_data?.profile_picture || '/api/placeholder/96/96'}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-graphite-900 font-semibold">{nomination.user_name || 'Unknown User'}</h3>
                      <p className="text-xs text-graphite-500">{nomination.user_data?.phone_number || 'N/A'}</p>
                      <p className="text-xs text-graphite-500">{nomination.user_data?.email || 'N/A'}</p>
                    </div>
                    <div className={`mr-4 px-4 py-1 rounded-full text-sm font-medium ${getStatusStyles(nomination.status)}`}>
                      {getStatusDisplayText(nomination.status)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <div className="bg-steel-50 rounded-xl p-3">
                  <p className="text-graphite-500">Requester</p>
                  <p className="text-sm text-graphite-900 font-medium">{nomination.company_representative_data || 'N/A'}</p>
                  <p className="text-sm text-graphite-700">{nomination.company_name || 'N/A'}</p>
                </div>
                <div className="bg-steel-50 rounded-xl p-3">
                  <p className="text-graphite-500">Validity</p>
                  <p className="text-sm text-graphite-900 font-medium">
                    {nomination.date_start && nomination.date_end ?
                      `${formatDate(nomination.date_start)} - ${formatDate(nomination.date_end)}` : 'N/A'}
                  </p>
                </div>
              </div>

              {(nomination.status === 'approved' || nomination.status === 'expired') && (
                <div className="bg-emerald-50 rounded-xl p-3 mt-2 text-xs border border-emerald-200">
                  <p className="text-graphite-500">Status</p>
                  <p className="text-sm text-graphite-900 font-medium">
                    Nomination approved by {nomination.approver_name || 'N/A'} on {formatDateTime(nomination.date_approved) || 'N/A'}
                  </p>
                  {nomination.status === 'expired' && (
                    <p className="text-sm text-graphite-700 mt-1">
                      Expired on: {formatDateTime(nomination.date_expired) || 'N/A'}
                    </p>
                  )}
                </div>
              )}

              {nomination.status === 'rejected' && (
                <div className="bg-rose-50 rounded-xl p-3 mt-2 text-xs border border-rose-200">
                  <p className="text-graphite-500">Status</p>
                  <p className="text-graphite-900 font-medium">
                    Nomination rejected by {nomination.approver_name || 'N/A'} on {formatDateTime(nomination.date_rejected) || 'N/A'}
                  </p>
                  <p className="text-graphite-500 mt-1">Rejection Reason</p>
                  <p className="text-graphite-900 font-medium">{nomination.comment || 'N/A'}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col shadow-sm">
            <button
              onClick={() => toggleSection('authorizationLevels')}
              className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200 hover:bg-steel-50 transition-colors"
            >
              <div className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-brand-500" />
                <span className="text-sm font-semibold text-graphite-900">Authorization Levels</span>
              </div>
              <div>
                {openSection === 'authorizationLevels' ?
                  <ChevronUp className="h-5 w-5 text-graphite-500" /> :
                  <ChevronDown className="h-5 w-5 text-graphite-500" />
                }
              </div>
            </button>
            
            {openSection === 'authorizationLevels' && (
              <div className="flex-1 overflow-y-auto max-h-[400px]">
                <div className="divide-y divide-steel-100">
                  {nomination.work_scope_data?.map((scope) => {
                    const isSelected = selectedWorkScopes.includes(scope.id);
                    const isActive = openWorkScopeIndex === scope.id || scope === activeScope;
                    const appliedCategories = getAppliedCategories(scope);

                    return (
                      <div key={scope.id}
                        className={`cursor-pointer transition-colors ${isActive ? 'bg-brand-50/50' : 'hover:bg-steel-50'}`}>
                        <div
                          className="p-3"
                          onClick={() => handleScopeClick(scope)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1">
                              {nomination.status === 'signed' ? (
                                <button
                                  className={`mr-3 ${isSelected ? 'text-brand-500' : 'text-graphite-600'} hover:text-brand-500 focus:outline-none transition-colors`}
                                  aria-label="Select authorization level"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleWorkScope(scope.id, scope.categories || []);
                                  }}>
                                  {isSelected ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-square-check h-5 w-5">
                                      <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                                      <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-square h-5 w-5">
                                      <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                                    </svg>
                                  )}
                                </button>
                              ) : nomination.status === 'rejected' ? (
                                <XCircle className="mr-3 h-5 w-5 text-rose-600" />
                              ) : (
                                <CheckCircle className="mr-3 h-5 w-5 text-emerald-600" />
                              )}
                              <div className="flex-1">
                                <h3 className="font-medium text-graphite-900">{scope.name}</h3>
                                {appliedCategories.length > 0 && (
                                  <span className="text-xs text-graphite-500">{appliedCategories.length} Platform{appliedCategories.length !== 1 ? 's' : ''}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {isActive && appliedCategories.length > 0 && (
                            <div className="mt-2 ml-8 space-y-1">
                              {appliedCategories.map((category) => {
                                const isCategorySelected = selectedCategories.includes(category.id);

                                return (
                                  <div
                                    key={category.id}
                                    className={`flex items-center p-2 rounded-xl cursor-pointer transition-colors ${
                                      isCategorySelected ? 'bg-brand-100' : 'hover:bg-brand-50'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleCategory(scope.id, category.id, scope.categories || []);
                                    }}
                                  >
                                    {nomination.status === 'signed' ? (
                                      <button
                                        className={`mr-2 ${isCategorySelected ? 'text-brand-500' : 'text-graphite-600'} hover:text-brand-500 focus:outline-none transition-colors`}
                                        aria-label="Select platform"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleCategory(scope.id, category.id, scope.categories || []);
                                        }}
                                      >
                                        {isCategorySelected ? (
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-square-check h-4 w-4">
                                            <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                                            <path d="m9 12 2 2 4-4"></path>
                                          </svg>
                                        ) : (
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-square h-4 w-4">
                                            <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                                          </svg>
                                        )}
                                      </button>
                                    ) : nomination.status === 'rejected' ? (
                                      <XCircle className="mr-2 h-4 w-4 text-rose-600" />
                                    ) : (
                                      <CheckCircle className="mr-2 h-4 w-4 text-emerald-600" />
                                    )}
                                    <span className="text-sm text-graphite-700">{category.name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {nomination.status === 'signed' && (
            <>
              <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col shadow-sm">
                <button
                  onClick={() => toggleSection('approvedNominations')}
                  className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200 hover:bg-steel-50 transition-colors"
                >
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-emerald-500" />
                    <span className="text-sm font-semibold text-graphite-900">Approved Nominations</span>
                  </div>
                  <div>
                    {openSection === 'approvedNominations' ?
                      <ChevronUp className="h-5 w-5 text-graphite-500" /> :
                      <ChevronDown className="h-5 w-5 text-graphite-500" />
                    }
                  </div>
                </button>
                
                {openSection === 'approvedNominations' && (
  <div className="p-4 space-y-2 flex-1 overflow-y-auto max-h-[400px]">
    {isLoadingNominations ? (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500 mr-2"></div>
        <span className="text-sm text-graphite-500">Loading nominations...</span>
      </div>
    ) : nominationsError ? (
      <p className="text-sm text-rose-500 italic">{nominationsError}</p>
    ) : approvedNominations.length > 0 ? (
      approvedNominations.map((nom) => (
        <div
          key={nom.id}
          className="p-3 bg-steel-50 rounded-xl border border-steel-200 hover:bg-steel-100 transition-colors"
        >
          <div className="flex justify-between items-center">
            <span className="font-semibold text-graphite-900">{nom.nomination_number || 'Nomination'}</span>
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border rounded-full text-xs font-medium">approved</Badge>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-sm text-graphite-600">
              Approver: <span className="font-medium text-graphite-700">{nom.approver_name || '-'}</span>
            </span>
            <span className="text-sm text-graphite-600">
              Expiry Date: <span className="font-medium text-graphite-700">{nom.date_end ? formatDate(nom.date_end) : '-'}</span>
            </span>
          </div>
          <div className="mt-2">
            <p className="text-sm text-graphite-600">
              Authorization levels:
              {nom.work_scope_data?.length > 0 ? (
                nom.work_scope_data.map((ws) => {
                  if (!ws.categories || ws.categories.length === 0) {
                    return (
                      <span key={ws.id} className="block text-sm text-graphite-700">
                        {ws.name} (All platforms)
                      </span>
                    );
                  }
                  const approvedCategories = ws.categories
                    .filter((cat) => nom.categories?.includes(cat.id))
                    .map((cat) => cat.name || `Category ${cat.id}`);
                  const allCategoriesApproved =
                    ws.categories.every((cat) => nom.categories?.includes(cat.id));
                  return (
                    <span key={ws.id} className="block text-sm text-graphite-700">
                      {ws.name} ({allCategoriesApproved ? 'All platforms' : approvedCategories.join(', ')})
                    </span>
                  );
                })
              ) : (
                <span className="block text-sm text-graphite-500"> -</span>
              )}
            </p>
          </div>
        </div>
      ))
    ) : (
      <p className="text-sm text-graphite-500 italic">No approved nominations for this user.</p>
    )}
  </div>
)}
              </div>

              <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col shadow-sm">
                <button
                  onClick={() => toggleSection('workPermits')}
                  className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200 hover:bg-steel-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-500" />
                    <span className="text-sm font-semibold text-graphite-900">Last Activity & Work Permits</span>
                  </div>
                  <div>
                    {openSection === 'workPermits' ?
                      <ChevronUp className="h-5 w-5 text-graphite-500" /> :
                      <ChevronDown className="h-5 w-5 text-graphite-500" />
                    }
                  </div>
                </button>

                {openSection === 'workPermits' && (
  <div className="p-4 space-y-2 flex-1 overflow-y-auto max-h-[400px]">
    {userWorkPermits.length > 0 ? (
      <>
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div className="p-3 bg-steel-50 rounded-xl">
            <p className="text-sm text-graphite-500">Finished Work Permits</p>
            <p className="font-semibold text-graphite-900">{userWorkPermits.length} Work permits</p>
          </div>
          <div className="p-3 bg-steel-50 rounded-xl">
            <p className="text-sm text-graphite-500">Last Activity</p>
            <p className="font-semibold text-graphite-900">{latestEndDate ? formatDate(latestEndDate) : '-'}</p>
          </div>
        </div>
        <div className="space-y-2">
          {userWorkPermits
            .sort((a, b) => {
              const dateA = a.planned_date_end ? new Date(a.planned_date_end) : new Date(0);
              const dateB = b.planned_date_end ? new Date(b.planned_date_end) : new Date(0);
              return dateB - dateA;
            })
            .slice(0, 20)
            .map((wp, index) => {
              let userRole = 'Team member';
              if (wp.team_data) {
                if (nomination.user === wp.team_data.team_leader) {
                  userRole = 'Supervisor';
                } else if (nomination.user === wp.team_data.person_responsible_for_preparation_of_work_place) {
                  userRole = 'Preparer';
                }
              }
              return (
                <div
                  key={index}
                  className="p-3 bg-steel-50 rounded-xl border border-steel-200 hover:bg-steel-100 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-graphite-900">
                      {wp.work_permit_number || `#${wp.id}`}
                    </span>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 border rounded-full text-xs font-medium">
                      {userRole}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-graphite-600">{wp.work_scope_data?.name || 'N/A'}</span>
                    <span className="text-sm text-graphite-600">
                      Finished: <span className="font-medium text-graphite-700">
                        {wp.planned_date_end ? formatDate(wp.planned_date_end) : '-'}
                      </span>
                    </span>
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-graphite-500">
                      {wp.turbine_data?.model_data?.name || 'Unknown'} /{' '}
                      {wp.turbine_data?.location || 'Unknown location'}
                    </p>
                  </div>
                </div>
              );
            })}
        </div>
      </>
    ) : (
      <p className="text-sm text-graphite-500 italic">No work permits available for this user.</p>
    )}
  </div>
)}
              </div>

              <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col shadow-sm">
                <button
                  onClick={() => toggleSection('warnings')}
                  className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200 hover:bg-steel-50 transition-colors"
                >
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />
                    <span className="text-sm font-semibold text-graphite-900">Warnings</span>
                  </div>
                  <div>
                    {openSection === 'warnings' ?
                      <ChevronUp className="h-5 w-5 text-graphite-500" /> :
                      <ChevronDown className="h-5 w-5 text-graphite-500" />
                    }
                  </div>
                </button>

                {openSection === 'warnings' && (
  <div className="p-4 space-y-2 flex-1 overflow-y-auto max-h-[400px]">
    {userWarnings.length > 0 ? (
      userWarnings
        .sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1))
        .map((warning) => (
          <div
            key={warning.id}
            className={`p-3 rounded-xl border ${
              warning.card_type_data?.name === 'Red Card'
                ? 'bg-rose-50 border-rose-200'
                : warning.card_type_data?.name === 'Yellow Card'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-steel-50 border-steel-200'
            } hover:bg-opacity-80 transition-colors`}
          >
            <div className="flex justify-between items-center">
              <span className="font-semibold text-graphite-900">
                Warning type: {warning.card_type_data?.name || 'Warning'}
              </span>
              <Badge
                className={`${
                  warning.active
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                } border rounded-full text-xs font-medium`}
              >
                {warning.active ? 'Active' : 'Expired'}
              </Badge>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-graphite-600">
                Issued by: <span className="font-medium text-graphite-700">{warning.punisher_name || '-'}</span>
              </span>
              <span className="text-sm text-graphite-600">
                Date Issued:{' '}
                <span className="font-medium text-graphite-700">
                  {warning.date_issued ? formatDate(warning.date_issued) : '-'}
                </span>
              </span>
            </div>
            <div className="mt-2">
              <p className="text-sm text-graphite-600">
                Reason: {warning.reason || '-'}
              </p>
            </div>
          </div>
        ))
    ) : (
      <p className="text-sm text-graphite-500 italic">No warnings available for this user.</p>
    )}
  </div>
)}
              </div>
            </>
          )}
        </div>

        <div className={`${columnWidths.middleCol} flex flex-col overflow-hidden transition-all duration-300`}>
  <div className="bg-transparent overflow-hidden flex-1 flex flex-col">
    <div className="p-4 bg-white rounded-2xl flex items-center border border-steel-200 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-graphite-900 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-brand-500" />
          Required Documents
        </h2>
        {activeScope && (
          <p className="text-sm font-medium text-brand-600 mt-1">
            {activeScope.name}{' '}
            {activeScope?.categories?.find(c => selectedCategories.includes(c.id))?.name &&
              `- ${activeScope?.categories?.find(c => selectedCategories.includes(c.id))?.name}`}
          </p>
        )}
      </div>
    </div>

    <div className="flex-1 overflow-auto mt-4 space-y-4">
      
      {/* SEKCJA PDF NOMINACJI */}
      {(nomination.pdf_file || true) && (
        <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col">
          <button
            onClick={toggleNominationPdf}
            className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200"
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-graphite-900" />
              <span className="text-sm font-medium text-graphite-900">Nomination Document</span>
            </div>
            <div>
              {isNominationPdfOpen ? 
                <ChevronUp className="h-5 w-5 text-graphite-500" /> : 
                <ChevronDown className="h-5 w-5 text-graphite-500" />
              }
            </div>
          </button>
          {isNominationPdfOpen && (
            <div className="p-4">
              <div
                className="p-2 flex items-center cursor-pointer hover:bg-steel-50 w-full"
                onClick={() => handlePreview('nomination-pdf', nomination.pdf_file, 'Nomination Document')}
              >
                <div className="mr-3">
                  <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-graphite-900">Nomination PDF</div>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-blue-600">Available</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeScope && requiredDocuments.length > 0 && (
        <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col">
          <button
            onClick={toggleAuthLevel}
            className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200"
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-graphite-900" />
              <span className="text-sm font-medium text-graphite-900">
                {activeScope.name} requirements (All Required)
              </span>
            </div>
            <div>
              {isAuthLevelOpen ? (
                <ChevronUp className="h-5 w-5 text-graphite-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-graphite-500" />
              )}
            </div>
          </button>
          {isAuthLevelOpen && (
            <div className={`grid ${previewExpanded ? 'grid-cols-1' : 'grid-cols-2'} gap-2 p-4`}>
              {requiredDocuments
                .sort((a, b) => (a.name || `Document ${a.id}`).localeCompare(b.name || `Document ${b.id}`))
                .map((doc) => {
                  const userDoc = userDocuments.find(uDoc => uDoc.id === doc.id);
                  const isValid = userDoc && userDoc.expiry_date && new Date(userDoc.expiry_date) > new Date();
                  const status = isValid ? 'Valid' : (userDoc && userDoc.expiry_date ? 'Expired' : 'Missing');

                  return (
                    <div
                      key={doc.id}
                      className="p-2 flex items-center cursor-pointer hover:bg-steel-50 w-full"
                      onClick={() => {
                        if (!userDoc?.certificate_file) {
                          alert("This user does not have the required certificate for this qualification.");
                        } else {
                          handlePreview(userDoc.id, userDoc.certificate_file, doc.name);
                        }
                      }}
                    >
                      <div className="mr-3">
                        <div className={`h-3 w-3 rounded-full ${isValid ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-graphite-900">{doc.name || `Document ${doc.id}`}</div>
                        <div className="flex items-center mt-1">
                          <span className={`text-xs ${isValid ? 'text-green-600' : 'text-amber-500'}`}>
                            {status}
                          </span>
                          {isValid && userDoc?.expiry_date && (
                            <span className="text-xs text-graphite-500 ml-3">
                              Expires: {formatDate(userDoc.expiry_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {categoryDocuments.map((catDocs, idx) => {
        const selectedCategory = activeScope?.categories?.find(c => c.name === catDocs.categoryName);
        const docGroups = selectedCategory?.required_doc_groups_category_data || [];

        return (
          <div key={idx} className="ml-8 bg-white rounded-2xl border border-steel-200 overflow-hidden flex flex-col">
            <button
              onClick={() => toggleCategory(idx)}
              className="w-full flex items-center justify-between p-4 text-left border-b border-steel-200"
            >
              <div className="flex items-center">
                <Wind className="h-5 w-5 mr-2 text-graphite-900" />
                <span className="text-sm font-medium text-graphite-900">
                  Platform {catDocs.categoryName} additional requirements
                </span>
              </div>
              <div>
                {openCategoryIdx === idx ? (
                  <ChevronUp className="h-5 w-5 text-graphite-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-graphite-500" />
                )}
              </div>
            </button>
            {openCategoryIdx === idx && (
              <div className="p-4">
                {/* Podsekcja: All Required */}
                {catDocs.documents.length > 0 ? (
                  <div>
                    <div className="p-2 bg-steel-50 text-xs font-medium text-graphite-600 uppercase tracking-wider">
                      All Required
                    </div>
                    <div className={`grid ${previewExpanded ? 'grid-cols-1' : 'grid-cols-2'} gap-2 p-4`}>
                      {catDocs.documents
                        .sort((a, b) => (a.name || `Document ${a.id}`).localeCompare(b.name || `Document ${b.id}`))
                        .map((doc) => {
                          const userDoc = userDocuments.find(uDoc => uDoc.id === doc.id);
                          const isValid = userDoc && userDoc.expiry_date && new Date(userDoc.expiry_date) > new Date();
                          const status = isValid ? 'Valid' : (userDoc && userDoc.expiry_date ? 'Expired' : 'Missing');

                          return (
                            <div
                              key={doc.id}
                              className="p-2 flex items-center cursor-pointer hover:bg-steel-50 w-full"
                              onClick={() => {
                                if (!userDoc?.certificate_file) {
                                  alert("This user does not have the required certificate for this qualification.");
                                } else {
                                  handlePreview(userDoc.id, userDoc.certificate_file, doc.name);
                                }
                              }}
                            >
                              <div className="mr-3">
                                <div className={`h-3 w-3 rounded-full ${isValid ? 'bg-green-500' : 'bg-amber-500'}`}></div>
                              </div>
                              <div className="flex-1">
                                <div className="text-sm text-graphite-900">{doc.name || `Document ${doc.id}`}</div>
                                <div className="flex items-center mt-1">
                                  <span className={`text-xs ${isValid ? 'text-green-600' : 'text-amber-500'}`}>
                                    {status}
                                  </span>
                                  {isValid && userDoc?.expiry_date && (
                                    <span className="text-xs text-graphite-500 ml-3">
                                      Expires: {formatDate(userDoc.expiry_date)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-graphite-500 text-center">
                    <p>No documents required for this category</p>
                  </div>
                )}

                {/* Podsekcja: Group Requirements */}
                {docGroups.length > 0 && (
                  <div className="mt-2">
                    <div className="p-2 bg-steel-50 text-xs font-medium text-graphite-600 uppercase tracking-wider">
                      Group Requirements (At Least One Required per Group)
                    </div>
                    <div className="mt-2 space-y-4">
                      {docGroups.map((group, groupIdx) => {
                        const hasValidDoc = group.some(doc => {
                          const userDoc = userDocuments.find(uDoc => uDoc.id === doc.id);
                          return userDoc && userDoc.expiry_date && new Date(userDoc.expiry_date) > new Date();
                        });

                        return (
                          <div
                            key={`group-${groupIdx}`}
                            className={`p-3 bg-steel-50 border rounded-xl ${hasValidDoc ? 'border-green-500' : 'border-amber-500'}`}
                          >
                            <div className="text-xs font-medium text-graphite-600 mb-2">
                              Group {groupIdx + 1} (At Least One Required)
                              <div
                                className="ml-3 mt-1 h-2 w-2 rounded-full inline-block"
                                style={{ backgroundColor: hasValidDoc ? '#22c55e' : '#f59e0b' }}
                              ></div>
                            </div>
                            <div className={`grid ${previewExpanded ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                              {group
                                .sort((a, b) => (a.name || `Document ${a.id}`).localeCompare(b.name || `Document ${b.id}`))
                                .map((doc) => {
                                  const userDoc = userDocuments.find(uDoc => uDoc.id === doc.id);
                                  const isValid = userDoc && userDoc.expiry_date && new Date(userDoc.expiry_date) > new Date();
                                  const status = isValid ? 'Valid' : (userDoc && userDoc.expiry_date ? 'Expired' : 'Missing');

                                  return (
                                    <div
                                      key={doc.id}
                                      className="px-4 py-2 flex items-center cursor-pointer hover:bg-steel-100 w-full"
                                      onClick={() => {
                                        if (!userDoc?.certificate_file) {
                                          alert("This user does not have the required certificate for this qualification.");
                                        } else {
                                          handlePreview(userDoc.id, userDoc.certificate_file, doc.name);
                                        }
                                      }}
                                    >
                                      <div className="mr-3">
                                        <div
                                          className={`h-3 w-3 rounded-full ${isValid ? 'bg-green-500' : 'bg-amber-500'}`}
                                        ></div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="text-sm text-graphite-900">
                                          {doc.name || `Document ${doc.id}`}
                                        </div>
                                        <div className="flex items-center mt-1">
                                          <span
                                            className={`text-xs ${isValid ? 'text-green-600' : 'text-amber-500'}`}
                                          >
                                            {status}
                                          </span>
                                          {isValid && userDoc?.expiry_date && (
                                            <span className="text-xs text-graphite-500 ml-3">
                                              Expires: {formatDate(userDoc.expiry_date)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      )}

      {!activeScope && (
        <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden p-4 text-graphite-500 text-center">
          <FileText className="h-10 w-10 mx-auto mb-2 text-graphite-300" />
          <p>Select an authorization level to view requirements</p>
        </div>
      )}

      {activeScope && !requiredDocuments.length && !categoryDocuments.length && (
        <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden p-4 text-graphite-500 text-center">
          <FileText className="h-10 w-10 mx-auto mb-2 text-graphite-300" />
          <p>No documents required for this authorization level</p>
        </div>
      )}
    </div>
  </div>
</div>

        <div className={`${columnWidths.rightCol} overflow-hidden transition-all duration-300 flex flex-col`}>
          {previewExpanded ? (
            <div className="h-full bg-white rounded-2xl border border-steel-200 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-steel-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-graphite-900">Document Preview</h2>
                  {previewDocName && ( // NOWE: Wyświetlenie nazwy certyfikatu
                    <p className="text-sm text-graphite-600 mt-1">{previewDocName}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={handleZoomOut}
                    className="p-1 rounded-full text-graphite-500 hover:bg-steel-100"
                    title="Zoom Out"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-zoom-out">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" x2="16.65" y1="21" y2="16.65"></line>
                      <line x1="8" x2="14" y1="11" y2="11"></line>
                    </svg>
                  </button>
                  <button 
                    onClick={handleZoomIn}
                    className="p-1 rounded-full text-graphite-500 hover:bg-steel-100"
                    title="Zoom In"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-zoom-in">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" x2="16.65" y1="21" y2="16.65"></line>
                      <line x1="11" x2="11" y1="8" y2="14"></line>
                      <line x1="8" x2="14" y1="11" y2="11"></line>
                    </svg>
                  </button>
                  <button 
                    onClick={handleClosePreview}
                    className="p-1 rounded-full text-graphite-500 hover:bg-steel-100"
                    title="Close Preview"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x">
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-4" ref={previewContainerRef}>
                {docPreviewUrl ? (
                  <Document
                    file={docPreviewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    className="h-full"
                  >
                    {Array.from(new Array(numPages || 0), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="mb-4 shadow-md"
                      />
                    ))}
                  </Document>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-graphite-500">Select a document to preview</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full bg-white rounded-2xl border border-steel-200 flex flex-col items-center cursor-pointer hover:bg-steel-100 transition-colors" onClick={handleExpandPreview}>
              <div className="p-4">
                <Eye className="h-6 w-6 text-graphite-400" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border-t-2 border-brand-500 px-4 py-3 flex justify-between items-center shadow-lg z-10 shrink-0">
        {nomination.status === 'signed' ? (
          <>
            <div className="text-sm text-graphite-600 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-brand-500" />
              <span>
                Selected: <strong className="text-graphite-900">{selectedWorkScopes.length}/{totalWorkScopes}</strong> authorization level{totalWorkScopes !== 1 ? 's' : ''} with{' '}
                <strong className="text-graphite-900">{selectedCategories.length}/{totalCategories}</strong> platform{totalCategories !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center">
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="px-6 py-2 bg-rose-600 font-medium text-white rounded-xl hover:bg-rose-700 transition-colors"
                >
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  className="px-6 py-2 bg-emerald-600 font-medium text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedWorkScopes.length === 0}
                >
                  Approve
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex w-full justify-end items-center">
            <button
              onClick={() => onClose(false)}
              className="px-6 py-2 bg-white font-medium text-graphite-600 border border-steel-300 rounded-xl hover:bg-steel-50 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>

      {showCodeInput && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-graphite-900 flex items-center mb-4">
              <Shield className="h-5 w-5 text-blue-500 mr-2" />
              Authentication Required
            </h2>
            <div className="mb-4">
              <p className="text-sm text-graphite-700 mb-2">{warningMessage}</p>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter verification code"
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCodeInput(false);
                  setVerificationCode('');
                  setVerificationDoc(null);
                  setWarningMessage('');
                }}
                className="px-4 py-2 border border-steel-300 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifyCode}
                className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                disabled={!verificationCode.trim()}
              >
                Verify
              </Button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-graphite-900 mb-4">Reject Nomination</h2>
            <div className="mb-4">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection"
                className="w-full p-3 border border-steel-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                rows={4}
              />
            </div>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 border border-steel-300 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                disabled={!rejectReason.trim()}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {showWarningModal && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-graphite-900 flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
              Warning
            </h2>
            <div className="mb-4">
              <p className="text-sm text-graphite-700 whitespace-pre-line">
                {warningMessage || "An error occurred. Please try again."}
              </p>
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={() => setShowWarningModal(false)}
                className="px-4 py-2 bg-graphite-800 text-white rounded-full hover:bg-graphite-700"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ECCNominationApprovalModal;